import { hijriParts, isRamadan, WEEKEND_DAYS, type CountryCode } from '@ly360/i18n';
import { Dialog, ErrorState } from '@ly360/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '../crm/forms';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { CalendarData } from '../pages/Calendar';

/** Date-only helpers. Dates are "YYYY-MM-DD" and handled at UTC noon, so no zone can shift them by a day. */
const noon = (d: string) => new Date(`${d}T12:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: string, n: number) => iso(new Date(noon(d).getTime() + n * 86_400_000));

/**
 * The company calendar as a month grid: weekends (from the organisation's country), public holidays, Ramadan,
 * Hijri dates and who is away. Clicking a day marks it as a holiday (admins) or adds time off for yourself.
 */
export function MonthView({ data, today }: { data: CalendarData; today: string }) {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [picked, setPicked] = useState<string | null>(null);
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const ar = lang === 'ar';
  const country = (session?.tenant.country ?? 'AE') as CountryCode;
  const weekend = WEEKEND_DAYS[country] ?? [6, 0];
  const weekStart = session?.tenant.defaults.weekStart ?? 0;
  const zone = session?.tenant.defaults.timezone ?? 'Asia/Dubai';

  const visible = useMemo(() => {
    const first = `${month}-01`;
    const [y, m] = month.split('-').map(Number) as [number, number];
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const lead = (noon(first).getUTCDay() - weekStart + 7) % 7;
    const start = addDays(first, -lead);
    return Array.from({ length: Math.ceil((lead + daysInMonth) / 7) * 7 }, (_, i) => addDays(start, i));
  }, [month, weekStart]);
  const weeks = visible.length / 7;

  const fmtMonth = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const fmtHijriMonth = new Intl.DateTimeFormat(`${lang}-u-ca-islamic-umalqura`, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const fmtWeekday = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' });
  const fmtDay = new Intl.DateTimeFormat(lang, { day: 'numeric', timeZone: 'UTC', numberingSystem: 'latn' });
  const fmtLong = new Intl.DateTimeFormat(lang, { dateStyle: 'full', timeZone: 'UTC' });
  const mid = noon(`${month}-15`);
  const hijriSpan = [
    ...new Set([
      fmtHijriMonth.format(noon(`${month}-01`)),
      fmtHijriMonth.format(noon(visible.filter((d) => d.startsWith(month)).at(-1)!)),
    ]),
  ];

  const holidayOn = (d: string) => data.holidays.find((h) => h.date === d);
  const awayOn = (d: string) => data.timeOff.filter((o) => o.startsOn <= d && o.endsOn >= d);
  const shift = (n: number) => {
    const [y, m] = month.split('-').map(Number) as [number, number];
    setMonth(iso(new Date(Date.UTC(y, m - 1 + n, 15))).slice(0, 7));
  };

  return (
    <section className="card month-view" aria-labelledby="month-title" data-testid="month-view">
      <div className="card-head month-head">
        <div style={{ minWidth: 0 }}>
          <h2 id="month-title">{fmtMonth.format(mid)}</h2>
          <div className="faint month-hijri">{hijriSpan.join(' – ')}</div>
        </div>
        <div className="quick-actions">
          <button
            type="button"
            className="icon-btn"
            onClick={() => shift(-1)}
            aria-label={t('calendar.month.previous')}
          >
            <ChevronLeft size={16} className="icon-dir" />
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMonth(today.slice(0, 7))}>
            {t('calendar.month.today')}
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => shift(1)}
            aria-label={t('calendar.month.next')}
          >
            <ChevronRight size={16} className="icon-dir" />
          </button>
        </div>
      </div>
      <div className="month-scroll">
        <div className="month-grid" role="grid" aria-labelledby="month-title">
          <div className="month-row month-weekdays" role="row">
            {visible.slice(0, 7).map((d) => (
              <div key={d} role="columnheader" className="month-wd">
                {fmtWeekday.format(noon(d))}
              </div>
            ))}
          </div>
          {Array.from({ length: weeks }, (_, w) => (
            <div key={w} className="month-row" role="row">
              {visible.slice(w * 7, w * 7 + 7).map((d) => {
                const date = noon(d);
                const holiday = holidayOn(d);
                const away = awayOn(d);
                const classes = [
                  'month-day',
                  d.slice(0, 7) !== month && 'is-other',
                  weekend.includes(date.getUTCDay()) && 'is-weekend',
                  holiday && 'is-holiday',
                  isRamadan(date, zone) && 'is-ramadan',
                  d === today && 'is-today',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    key={d}
                    type="button"
                    role="gridcell"
                    className={classes}
                    onClick={() => setPicked(d)}
                    aria-label={`${fmtLong.format(date)}${holiday ? `, ${(ar && holiday.nameAr) || holiday.name}` : ''}`}
                    data-date={d}
                  >
                    <span className="md-num">{fmtDay.format(date)}</span>
                    <span className="md-hijri">{hijriParts(date, zone).day}</span>
                    {holiday && (
                      <span className="md-holiday" dir="auto">
                        {(ar && holiday.nameAr) || holiday.name}
                      </span>
                    )}
                    {away.length > 0 && (
                      <span className="md-away" title={away.map((o) => o.displayName).join(', ')}>
                        {t('calendar.month.away', { count: away.length })}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="month-legend faint">
        <span>
          <i className="lg lg-weekend" /> {t('calendar.month.weekend')}
        </span>
        <span>
          <i className="lg lg-holiday" /> {t('calendar.month.holiday')}
        </span>
        <span>
          <i className="lg lg-ramadan" /> {t('calendar.month.ramadan')}
        </span>
      </div>
      {picked && (
        <DayDialog
          date={picked}
          data={data}
          onClose={() => setPicked(null)}
          label={fmtLong.format(noon(picked))}
        />
      )}
    </section>
  );
}

function DayDialog({
  date,
  data,
  label,
  onClose,
}: {
  date: string;
  data: CalendarData;
  label: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const ar = i18n.language === 'ar';
  const holiday = data.holidays.find((h) => h.date === date);
  const away = data.timeOff.filter((o) => o.startsOn <= date && o.endsOn >= date);
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['calendar'] });
    void qc.invalidateQueries({ queryKey: ['service-levels'] });
    onClose();
  };
  const addHoliday = useMutation({
    mutationFn: () =>
      api('/v1/holidays', {
        method: 'POST',
        body: JSON.stringify({ date, name, nameAr: nameAr.trim() || null }),
      }),
    onSuccess: done,
  });
  const removeHoliday = useMutation({
    mutationFn: () => api(`/v1/holidays/${holiday!.id}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const offToday = useMutation({
    mutationFn: () =>
      api('/v1/time-off', { method: 'POST', body: JSON.stringify({ startsOn: date, endsOn: date }) }),
    onSuccess: done,
  });
  const error = addHoliday.error ?? removeHoliday.error ?? offToday.error;
  return (
    <Dialog open title={label} onClose={onClose} testId="day-dialog">
      {holiday ? (
        <div className="day-holiday">
          <strong dir="auto">{(ar && holiday.nameAr) || holiday.name}</strong>
          <span className="faint">{t('calendar.month.isHoliday')}</span>
          {data.canManageHolidays && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => removeHoliday.mutate()}
              disabled={removeHoliday.isPending}
            >
              {t('calendar.month.unmark')}
            </button>
          )}
        </div>
      ) : (
        data.canManageHolidays && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addHoliday.mutate();
            }}
            className="form-grid"
            data-testid="mark-holiday"
          >
            <Field id="day-hol-name" label={t('sla.holidayName')}>
              <input
                id="day-hol-name"
                className="input"
                required
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field id="day-hol-name-ar" label={t('sla.holidayNameAr')}>
              <input
                id="day-hol-name-ar"
                className="input"
                dir="rtl"
                lang="ar"
                maxLength={120}
                value={nameAr}
                onChange={(e) => setNameAr(e.target.value)}
              />
            </Field>
            <div className="field" style={{ alignContent: 'end' }}>
              <button
                type="submit"
                className="btn btn-grad"
                disabled={addHoliday.isPending}
                style={{ justifySelf: 'start' }}
              >
                {t('calendar.month.mark')}
              </button>
            </div>
          </form>
        )
      )}
      <h3 className="section-title" style={{ marginTop: '1rem' }}>
        {t('calendar.whoIsAway')}
      </h3>
      <ul className="list-plain cal-list">
        {away.map((o) => (
          <li key={o.id}>
            <span dir="auto">{(ar && o.displayNameAr) || o.displayName}</span>
            {o.note && (
              <span className="faint" dir="auto">
                {o.note}
              </span>
            )}
          </li>
        ))}
        {!away.length && <li className="faint">{t('calendar.month.nobody')}</li>}
      </ul>
      {!away.some((o) => o.userId === data.me.userId) && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => offToday.mutate()}
          disabled={offToday.isPending}
          data-testid="off-this-day"
        >
          {t('calendar.month.imOff')}
        </button>
      )}
      {error && <ErrorState error={error} />}
    </Dialog>
  );
}
